import { useCallback, useMemo, useRef } from "react";
import { getUserAssets, type UserAssetRecord } from "@apis/http/api";
import {
  PathsApiUsersAssetsGetParametersQueryAssetSource,
  PathsApiUsersAssetsGetParametersQueryAssetType,
} from "@apis/generated/openapi";
import { useScrollLoad, type ScrollPageResult } from "@hooks/useScrollLoad";
import { LIBRARY_PAGE_SIZE, type PostVideoLibrarySubTab } from "./constants";

interface ScrollState {
  scrollTop: number;
}

export interface PostVideoLibraryTabState {
  items: UserAssetRecord[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  scrollTop: number;
  sentinelRef: (node: HTMLElement | null) => void;
}

export interface UsePostVideoLibraryOptions {
  enabled: boolean;
  subTab: PostVideoLibrarySubTab;
}

export interface UsePostVideoLibraryResult extends PostVideoLibraryTabState {
  saveScrollTop: (tab: PostVideoLibrarySubTab, scrollTop: number) => void;
  refresh: () => void;
}

const createAssetFetcher =
  (assetSource: PathsApiUsersAssetsGetParametersQueryAssetSource) =>
  async ({ pageNo, pageSize }: { pageNo: number; pageSize: number }): Promise<ScrollPageResult<UserAssetRecord>> => {
    const response = await getUserAssets({
      assetType: PathsApiUsersAssetsGetParametersQueryAssetType.VID,
      assetSource,
      pageNo,
      pageSize,
    });
    const data = (response?.data ?? []).filter((record) => record.state === undefined || record.state === 1);

    return {
      data,
      pageNo: Number(response?.pageNo ?? pageNo),
      pageSize: Number(response?.pageSize ?? pageSize),
      total: Number(response?.total ?? data.length),
    };
  };

export const usePostVideoLibrary = ({ enabled, subTab }: UsePostVideoLibraryOptions): UsePostVideoLibraryResult => {
  const generated = useScrollLoad<UserAssetRecord, Record<string, never>>({
    enabled: enabled && subTab === "generated",
    params: {},
    pageSize: LIBRARY_PAGE_SIZE,
    fetcher: createAssetFetcher(PathsApiUsersAssetsGetParametersQueryAssetSource.AI),
  });
  const uploaded = useScrollLoad<UserAssetRecord, Record<string, never>>({
    enabled: enabled && subTab === "uploaded",
    params: {},
    pageSize: LIBRARY_PAGE_SIZE,
    fetcher: createAssetFetcher(PathsApiUsersAssetsGetParametersQueryAssetSource.USER),
  });

  const scrollStateRef = useRef<Record<PostVideoLibrarySubTab, ScrollState>>({
    generated: { scrollTop: 0 },
    uploaded: { scrollTop: 0 },
  });

  const saveScrollTop = useCallback((tab: PostVideoLibrarySubTab, scrollTop: number) => {
    scrollStateRef.current[tab] = { scrollTop };
  }, []);

  const refresh = useCallback(() => {
    if (subTab === "generated") {
      generated.refresh();
      return;
    }
    uploaded.refresh();
  }, [generated, subTab, uploaded]);

  return useMemo(() => {
    const scrollTop = scrollStateRef.current[subTab].scrollTop;
    if (subTab === "generated") {
      return {
        items: generated.items,
        loading: generated.loading,
        loadingMore: generated.loadingMore,
        hasMore: generated.hasMore,
        error: generated.error,
        scrollTop,
        sentinelRef: generated.sentinelRef,
        saveScrollTop,
        refresh,
      };
    }
    return {
      items: uploaded.items,
      loading: uploaded.loading,
      loadingMore: uploaded.loadingMore,
      hasMore: uploaded.hasMore,
      error: uploaded.error,
      scrollTop,
      sentinelRef: uploaded.sentinelRef,
      saveScrollTop,
      refresh,
    };
  }, [generated, refresh, saveScrollTop, subTab, uploaded]);
};
